import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import WorkoutTimer from './WorkoutTimer';
import IntensitySelector from './IntensitySelector';
import UserInfo from './UserInfo';
import ProgramDetails from './ProgramDetails';
import WorkoutOverlay from './WorkoutOverlay';
import { useWorkoutTimer } from '../hooks/useWorkoutTimer';
import { useWorkoutHistory } from '../hooks/useWorkoutHistory';
import { calculateStepLength } from '../utils/workoutCalculator';
import { useLanguage } from '../context/LanguageContext';
import { useRadio } from '../context/RadioContext';

const { FiSettings, FiMaximize, FiClock, FiMusic, FiRadio } = FiIcons;

const WorkoutDashboard = ({ userProfile, workoutProgram, selectedIntensity, onIntensityChange }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isPlaying } = useRadio();
  const [showOverlay, setShowOverlay] = useState(false);
  const [greeting, setGreeting] = useState('');
  
  const { saveSession } = useWorkoutHistory();

  const handleWorkoutComplete = useCallback((finalDistance, durationSec) => {
    if (durationSec < 60) return; 

    const stepLengthM = calculateStepLength(userProfile.sex, userProfile.height_cm);
    const steps = Math.round(finalDistance / stepLengthM);
    
    saveSession({
      intensity: selectedIntensity,
      duration: durationSec / 60,
      distance: finalDistance / 1000,
      steps: steps,
      avgSpeed: 0
    });
  }, [userProfile, selectedIntensity, saveSession]);

  const currentSessionRef = useRef({ distance: 0, time: 0 });

  const timerData = useWorkoutTimer(workoutProgram, () => {
    handleWorkoutComplete(currentSessionRef.current.distance, currentSessionRef.current.time);
    setShowOverlay(false);
    alert(t('congrats'));
  });

  useEffect(() => {
    currentSessionRef.current = {
      distance: timerData.distanceTraveled,
      time: timerData.totalElapsed
    };
  }, [timerData.distanceTraveled, timerData.totalElapsed]);

  const handleManualStop = () => {
    if (timerData.totalElapsed > 60) {
      if(window.confirm(t('stop_confirm'))) {
        handleWorkoutComplete(timerData.distanceTraveled, timerData.totalElapsed);
      }
    }
    timerData.handleStop();
    setShowOverlay(false);
  };

  const wrappedTimerData = {
    ...timerData,
    handleStop: handleManualStop
  };

  useEffect(() => {
    if (userProfile) {
      const hour = new Date().getHours();
      const firstName = userProfile.firstName || (userProfile.sex === 'femeie' ? t('dear_female') : t('dear_male'));
      const genderGreeting = userProfile.sex === 'femeie' ? t('welcome_female') : t('welcome');
      
      let timeGreeting = '';
      if (hour >= 5 && hour < 12) {
        timeGreeting = t('morning');
      } else if (hour >= 18 || hour < 5) {
        timeGreeting = t('evening');
      }
      
      if (timeGreeting) {
        setGreeting(`${genderGreeting} ${firstName}, ${timeGreeting}`);
      } else {
        setGreeting(`${genderGreeting}, ${firstName}`);
      }
    }
  }, [userProfile, t]);

  useEffect(() => {
    if (timerData.isActive && !timerData.isPaused && timerData.totalElapsed === 1) {
      setShowOverlay(true);
    }
  }, [timerData.isActive, timerData.isPaused, timerData.totalElapsed]);

  const getIntensityMessage = (intensity) => {
    switch (intensity) {
      case 'start': return t('msg_start');
      case 'usor': return t('msg_easy');
      case 'mediu': return t('msg_medium');
      case 'intens': return t('msg_intense');
      default: return t('msg_default');
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8 pb-20">
      <AnimatePresence>
        {showOverlay && (
          <WorkoutOverlay 
            isVisible={showOverlay} 
            onClose={() => setShowOverlay(false)} 
            timerData={wrappedTimerData}
          />
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto">
        {/* Top Bar */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight capitalize">
              {greeting} 👋
            </h1>
            <p className="text-slate-500 mt-1 text-lg">
              {getIntensityMessage(selectedIntensity)}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => navigate('/radio')}
              className={`flex items-center gap-2 px-5 py-2.5 border rounded-xl transition-all active:scale-95 ${isPlaying ? 'bg-rose-50 text-rose-600 border-rose-200 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            >
              <SafeIcon icon={FiRadio} className={isPlaying ? "animate-pulse" : ""} />
              <span className="font-medium">{t('radio')}</span>
            </button>

            <button 
              onClick={() => navigate('/history')}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-xl hover:bg-blue-100 transition-all active:scale-95"
            >
              <SafeIcon icon={FiClock} />
              <span className="font-medium">{t('history')}</span>
            </button>
            
            {timerData.isActive && (
              <button 
                onClick={() => setShowOverlay(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 active:scale-95"
              >
                <SafeIcon icon={FiMaximize} />
                <span className="font-medium">{t('fullscreen')}</span>
              </button>
            )}

            <button 
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-all active:scale-95"
            >
              <SafeIcon icon={FiSettings} />
              <span className="font-medium">{t('profile')}</span>
            </button>
          </div>
        </header>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Column: Controls & Info 
              ORDER CHANGED: order-2 on mobile, order-1 on desktop
          */}
          <div className="lg:col-span-3 flex flex-col gap-6 order-2 lg:order-1">
            <IntensitySelector 
              selectedIntensity={selectedIntensity} 
              onIntensityChange={onIntensityChange}
              disabled={timerData.isActive}
              fitnessLevel={userProfile?.fitnessLevel}
            />
            <UserInfo userProfile={userProfile} />
          </div>

          {/* Center Column: Big Timer 
              ORDER CHANGED: order-1 on mobile (FIRST), order-2 on desktop (MIDDLE)
          */}
          <div className="lg:col-span-6 min-h-[500px] order-1 lg:order-2">
            <WorkoutTimer timerData={wrappedTimerData} workoutProgram={workoutProgram} />
          </div>

          {/* Right Column: Details 
              ORDER CHANGED: order-3 (LAST)
          */}
          <div className="lg:col-span-3 order-3 lg:order-3">
            <ProgramDetails workoutProgram={workoutProgram} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutDashboard;